import React, { useState } from "react";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiHeadphones,
  FiMail,
} from "react-icons/fi";
import axios from "axios";
import { Link } from "react-router-dom";
import "../../ComponentsCss/Support/Support.css";

const SUPPORT_API_URL = `${process.env.REACT_APP_API_URL}/support-queries`;

const frequentlyAskedQuestions = [
  {
    category: "Blogs",
    question: "Why isn't my blog showing on the social media page?",
    answer:
      "Check that the blog is published and that you are viewing the correct social page. If it was just published, refresh the page and allow a moment for the latest posts to load.",
  },
  {
    category: "Blog images",
    question: "Why does my blog image look cropped or blurry?",
    answer:
      "Social platforms may crop images to fit their layouts. Use a clear, high-resolution image and preview the post before publishing to check how it appears.",
  },
  {
    category: "Titles & descriptions",
    question: "Why is my blog title or description cut off?",
    answer:
      "Some social platforms limit the amount of text shown in a post preview. Keep the key information near the beginning of your title and description.",
  },
  {
    category: "Titles & descriptions",
    question: "Can I change a blog's title and description?",
    answer:
      "Yes. Update the title and description in the blog editor, save your changes, and check the social post preview before sharing it.",
  },
  {
    category: "Weather",
    question: "Why is the weather information missing or incorrect?",
    answer:
      "Confirm that the correct location is selected and refresh the page. Weather information can vary if the location or its settings have changed.",
  },
  {
    category: "Step counts",
    question: "Why aren't my step counts updating?",
    answer:
      "Check that activity tracking is enabled and that your device or app has synced recently. Refresh the page after the next sync to see the latest count.",
  },
  {
    category: "Step counts",
    question: "Why is my step count different from my device?",
    answer:
      "Step totals may differ when devices sync at different times or use different day boundaries. Check the date and last sync time on both before comparing.",
  },
];

function Support() {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");

    const formData = new FormData(event.currentTarget);
    const query = {
      name: formData.get("name"),
      email: formData.get("email"),
      topic: formData.get("topic"),
      subject: formData.get("subject"),
      description: formData.get("description"),
    };

    try {
      const response = await axios.post(SUPPORT_API_URL, query, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data?.success === false) {
        throw new Error(
          response.data.message || "We couldn’t send your support request.",
        );
      }

      setSubmitted(true);
    } catch (error) {
      console.error("Unable to submit support request:", error);
      setSubmitError(
        error.response?.data?.message ||
          error.message ||
          "We couldn’t send your support request. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="support-page">
      <header className="support-header">
        <div className="support-header-inner">
          <Link className="support-brand" to="/login" aria-label="VNG Group home">
            <span className="support-brand-mark">V</span>
            <span>
              VNG<span className="support-brand-light"> Group</span>
            </span>
          </Link>
          <nav className="support-nav" aria-label="Main navigation">
            <Link className="support-nav-link" to="/social-gathering">
              Social gathering
            </Link>
            <Link
              className="support-nav-link support-nav-link-active"
              to="/support"
              aria-current="page"
            >
              Support
            </Link>
          </nav>
          <Link className="support-back-link" to="/login">
            <FiArrowLeft aria-hidden="true" />
            Back to sign in
          </Link>
        </div>
      </header>

      <div className="support-content">
        <section className="support-hero" aria-labelledby="support-title">
          <div className="support-icon-wrap" aria-hidden="true">
            <FiHeadphones />
          </div>
          <p className="support-eyebrow">SUPPORT CENTER</p>
          <h1 id="support-title">How can we help?</h1>
          <p className="support-description">
            Browse quick answers to common questions, or contact our team if you
            need a hand.
          </p>
        </section>

        <section
          className="support-faq-section"
          aria-labelledby="support-faq-title"
        >
          <div className="support-section-heading">
            <p className="support-eyebrow">HELPFUL ANSWERS</p>
            <h2 id="support-faq-title">Frequently asked questions</h2>
            <p>
              Tips for blogs, social posts, weather updates, and step counts.
            </p>
          </div>
          <div className="support-faq-list">
            {frequentlyAskedQuestions.map((item) => (
              <details className="support-faq-item" key={item.question}>
                <summary>
                  <span className="support-faq-category">{item.category}</span>
                  <span className="support-faq-question">{item.question}</span>
                  <span className="support-faq-toggle" aria-hidden="true" />
                </summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section
          className="support-contact-section"
          aria-labelledby="support-contact-title"
        >
          <div className="support-contact-intro">
            <p className="support-eyebrow">STILL NEED A HAND?</p>
            <h2 id="support-contact-title">Contact our support team</h2>
            <p>
              Tell us what happened and we’ll help you find the right solution.
            </p>
            <div className="support-note">
              <div className="support-note-icon" aria-hidden="true">
                <FiMail />
              </div>
              <div>
                <h3>Prefer email?</h3>
                <p>
                  Send your question to{" "}
                  <a
                    className="support-email-link"
                    href="mailto:no-reply@vnggroup.co.uk"
                  >
                    no-reply@vnggroup.co.uk
                  </a>
                </p>
              </div>
            </div>
          </div>

          <div className="support-form-card">
            <div className="support-form-heading">
              <p className="support-form-kicker">CONTACT SUPPORT</p>
              <h3>Tell us about your issue</h3>
              <p>
                Fields marked with <span aria-hidden="true">*</span> are
                required.
              </p>
            </div>

            {submitted ? (
              <div className="support-preview-message" role="status">
                <FiCheckCircle aria-hidden="true" />
                <div>
                  <h4>Your request has been received</h4>
                  <p>
                    Our support team will review your message and get back to
                    you as soon as possible.
                  </p>
                </div>
              </div>
            ) : (
              <form className="support-form" onSubmit={handleSubmit}>
                <div className="support-field-row">
                  <label className="support-field">
                    <span>
                      Your name <span aria-hidden="true">*</span>
                    </span>
                    <input
                      type="text"
                      name="name"
                      placeholder="e.g. Alex Morgan"
                      autoComplete="name"
                      required
                    />
                  </label>
                  <label className="support-field">
                    <span>
                      Your email <span aria-hidden="true">*</span>
                    </span>
                    <input
                      type="email"
                      name="email"
                      placeholder="you@company.com"
                      autoComplete="email"
                      required
                    />
                  </label>
                </div>

                <label className="support-field">
                  <span>
                    What is your issue related to?{" "}
                    <span aria-hidden="true">*</span>
                  </span>
                  <select name="topic" defaultValue="" required>
                    <option value="" disabled>
                      Select an issue type
                    </option>
                    <option value="login">Login</option>
                    <option value="blog">Blog</option>
                    <option value="blog-image">Blog image</option>
                    <option value="weather">Weather</option>
                    <option value="steps">Steps</option>
                  </select>
                </label>

                <label className="support-field">
                  <span>
                    Subject <span aria-hidden="true">*</span>
                  </span>
                  <input
                    type="text"
                    name="subject"
                    placeholder="A short summary of the issue"
                    required
                  />
                </label>

                <label className="support-field">
                  <span>
                    How can we help? <span aria-hidden="true">*</span>
                  </span>
                  <textarea
                    name="description"
                    placeholder="Describe what happened and what you were trying to do..."
                    rows="5"
                    required
                  />
                </label>

                {submitError && (
                  <p className="support-form-error" role="alert">
                    {submitError}
                  </p>
                )}

                <button
                  className="support-submit"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending request..." : "Send support request"}
                  {!isSubmitting && <span aria-hidden="true">→</span>}
                </button>
                <p className="support-form-disclaimer">
                  Your details will be sent securely to our support team.
                </p>
              </form>
            )}
          </div>
        </section>
      </div>

      <footer className="support-footer">
        <div className="support-footer-inner">
          <span className="support-footer-brand">VNG Group</span>
          <span>
            © {new Date().getFullYear()} VNG Group. All rights reserved.
          </span>
          <Link to="/social-gathering">Social gathering</Link>
        </div>
      </footer>
    </main>
  );
}

export default Support;
